import dayjs, { Dayjs } from "dayjs";
import { FilterValue } from "../../Filters/FilterValue";
import { SortType } from "../../SortType";
import { OrderTrackingStage } from "./OrderTrackingAnalytics";

export interface OrderTrackingAnalyticsFilters {
  start_date: Dayjs;
  end_date: Dayjs;
  order_type_id: FilterValue[];
  order_id: FilterValue[];
  sales_poc_id: FilterValue[];
  pm_poc_id: FilterValue[];
  customer_id: FilterValue[];
  stage: OrderTrackingStage;
  page_no: number;
  limit: number;
  sort_by: string;
  sort_type: SortType;
}

export interface OrderTrackingAnalyticsAPIFilters {
  start_date: string;
  end_date: string;
  order_type_id: FilterValue[];
  order_id: FilterValue[];
  sales_poc_id: FilterValue[];
  pm_poc_id: FilterValue[];
  customer_id: FilterValue[];
  stage?: OrderTrackingStage;
  page_no?: number;
  limit?: number;
  sort_by?: string;
  sort_type?: SortType;
}

const parseStage = (value: string | null): OrderTrackingStage => {
  if (value === "all") {
    return "all";
  }
  if (
    value === "project_pending" ||
    value === "pr_pending" ||
    value === "raw_inward_pending" ||
    value === "print_outward_pending" ||
    value === "print_inward_pending" ||
    value === "dispatch_pending"
  ) {
    return value;
  }
  return "project_pending";
};

const parseFilterValues = (
  searchParams: URLSearchParams,
  key: string,
): FilterValue[] => {
  const raw = searchParams.get(key);
  if (!raw) return [];
  return raw
    .split(",")
    .filter(Boolean)
    .map((id) => ({ id, value: id }));
};

export const toOrderTypeFilterValues = (
  orderTypes: Array<{ id: string; value: string }>,
): FilterValue[] =>
  orderTypes.map((item) => ({ id: item.id, value: item.value }));

export const defaultOrderTrackingAnalyticsFilters =
  (): OrderTrackingAnalyticsFilters => ({
    start_date: dayjs().startOf("month"),
    end_date: dayjs(),
    order_type_id: [],
    order_id: [],
    sales_poc_id: [],
    pm_poc_id: [],
    customer_id: [],
    stage: "project_pending",
    page_no: 1,
    limit: 30,
    sort_by: "project_created_date",
    sort_type: SortType.ASC,
  });

export function parseQueryParamsForOrderTrackingAnalyticsFilters(
  searchParams: URLSearchParams,
): OrderTrackingAnalyticsFilters {
  const defaults = defaultOrderTrackingAnalyticsFilters();
  const sortTypeParam = searchParams.get("sort_type");

  return {
    start_date: searchParams.get("start_date")
      ? dayjs(searchParams.get("start_date"), "YYYY-MM-DD")
      : defaults.start_date,
    end_date: searchParams.get("end_date")
      ? dayjs(searchParams.get("end_date"), "YYYY-MM-DD")
      : defaults.end_date,
    order_type_id: parseFilterValues(searchParams, "order_type_id").length
      ? parseFilterValues(searchParams, "order_type_id")
      : defaults.order_type_id,
    order_id: parseFilterValues(searchParams, "order_id"),
    sales_poc_id: parseFilterValues(searchParams, "sales_poc_id"),
    pm_poc_id: parseFilterValues(searchParams, "pm_poc_id"),
    customer_id: parseFilterValues(searchParams, "customer_id"),
    stage: parseStage(searchParams.get("stage")),
    page_no: searchParams.get("page_no")
      ? Number(searchParams.get("page_no")) || defaults.page_no
      : defaults.page_no,
    limit: searchParams.get("limit")
      ? Number(searchParams.get("limit")) || defaults.limit
      : defaults.limit,
    sort_by: searchParams.get("sort_by") || defaults.sort_by,
    sort_type:
      sortTypeParam === SortType.ASC || sortTypeParam === SortType.DESC
        ? sortTypeParam
        : defaults.sort_type,
  };
}
